import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useGameStore } from '../../store/gameStore';
import { gameAPI } from '../../services/gameAPI';
import { parseActionError } from '../../utils/actionError';
import { APPROACH_FIT_CAPTION, TONE_CLASSES, approachFit } from '../../utils/effectiveness';
import {
  Heart,
  Flame,
  Brain,
  EyeOff,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  MessageSquare
} from 'lucide-react';

// Clamp a round number to the valid 1..2 range (UI/event labels can't represent
// anything outside this range).
const clampRound = (value) => Math.min(Math.max(value, 1), 2);

// F4 abort-recovery: after a client-side timeout/abort of a conduct request,
// the server may still finish and commit the round. Poll server truth for up
// to MAX_POLLS * POLL_MS before falling back to the error UI.
const INTERROGATION_ABORT_RECOVERY_MAX_POLLS = 8;
const INTERROGATION_ABORT_RECOVERY_POLL_MS = 5000;

const APPROACHES = [
  {
    id: 'sympathetic',
    name: 'Sympathetic',
    icon: Heart,
    description: 'Build rapport and trust with the suspect.',
    detail: 'Show understanding and empathy to make them feel safe.',
    color: '#4CAF50'
  },
  {
    id: 'aggressive',
    name: 'Aggressive',
    icon: Flame,
    description: 'Apply pressure and demand answers.',
    detail: 'Confront them directly and intimidate.',
    color: '#f44336'
  },
  {
    id: 'logical',
    name: 'Logical',
    icon: Brain,
    description: 'Present facts and inconsistencies.',
    detail: 'Appeal to reason and point out contradictions.',
    color: '#2196F3'
  },
  {
    id: 'deceptive',
    name: 'Deceptive',
    icon: EyeOff,
    description: 'Bluff about evidence to trick revelations.',
    detail: 'Imply you know more than you do.',
    color: '#9C27B0'
  }
];

export const InterrogationModal = ({
  open,
  onClose,
  suspect,
  onComplete,
  initialRoundsCompleted = 0,
  isTutorial = false,
  tutorialNarratives = {}, // For tutorial: pre-written interrogation narratives
  recommendedApproach = null, // For tutorial: 'logical', 'aggressive', 'sympathetic', 'deceptive'
  onInterrogationAction = null, // For tutorial: callback for sub-step events
  onRefetchSession = null
}) => {
  const [selectedApproach, setSelectedApproach] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [result, setResult] = useState(null);
  const [personalityHint, setPersonalityHint] = useState('');
  const [currentRound, setCurrentRound] = useState(initialRoundsCompleted + 1);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [lastFailedAction, setLastFailedAction] = useState(null); // 'init' | 'conduct' | null
  const [allResults, setAllResults] = useState([]); // Track all round results

  const sessionId = useGameStore((state) => state.sessionId);
  const selectedTheme = useGameStore((state) => state.selectedTheme);

  // Theme accents
  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493'
  }[selectedTheme] || '#DC143C';

  // Sync state when initialRoundsCompleted prop changes
  useEffect(() => {
    // Clamp to 1-2 range to prevent UI/event mislabeling
    const clampedRound = clampRound(initialRoundsCompleted + 1);
    setCurrentRound(clampedRound);
  }, [initialRoundsCompleted]);

  // Tutorial: Emit event when results are displayed
  useEffect(() => {
    if (result && onInterrogationAction) {
      // Clamp to 1-2 range to prevent emitting non-existent event types
      const clampedRound = clampRound(result.round_completed);
      onInterrogationAction(
        clampedRound === 1 ? 'interrogation_round1_results' : 'interrogation_round2_results',
        suspect?.id
      );
    }
  }, [result, onInterrogationAction, suspect?.id]);

  // Initialize interrogation when modal opens
  useEffect(() => {
    if (open && suspect && initialRoundsCompleted === 0) {
      initializeInterrogation();
    } else if (open && suspect && initialRoundsCompleted === 1) {
      // Coming back for Round 2
      setCurrentRound(2);
    }
  }, [open, suspect, initialRoundsCompleted]);

  const initializeInterrogation = async () => {
    setInitializing(true);
    setError(null);
    setErrorDetails(null);
    try {
      if (isTutorial) {
        // Tutorial mode: No API calls, use personality hint from suspect data or generic
        const hint = suspect.personality
          ? `This suspect has a ${suspect.personality} personality type. Choose your approach carefully.`
          : 'Consider the suspect\'s background and choose your interrogation approach.';
        setPersonalityHint(hint);
        // Clamp to 1-2 range
        setCurrentRound(clampRound((initialRoundsCompleted || 0) + 1));
      } else {
        // Real game: Use API
        const response = await gameAPI.initInterrogation(sessionId, suspect.id);
        setPersonalityHint(response.personality_hint || '');
        if (response.already_complete && Array.isArray(response.rounds)) {
          // Resumable backend: this suspect is fully interrogated (our
          // rounds-completed state was stale). Render the stored rounds
          // read-only via the existing result view.
          const narratives = response.rounds.map((r) => r?.narrative).filter(Boolean);
          const lastRound = response.rounds[response.rounds.length - 1] || {};
          setResult({
            narrative: narratives.join('\n\n---\n\n')
              || 'This suspect has already been fully interrogated.',
            effectiveness: lastRound.effectiveness ?? 0.5,
            effectiveness_feedback: '',
            revealed: { new_suspects: [], new_clues: [], new_locations: [], new_documents: [] },
            approach: lastRound.approach,
            round_completed: 2,
            can_continue: false,
            already_complete: true,
          });
          setCurrentRound(2);
          return;
        }
        // Clamp to 1-2 range
        setCurrentRound(clampRound((response.rounds_completed || 0) + 1));
      }
    } catch (err) {
      console.error('Error initializing interrogation:', err);
      const parsed = parseActionError(err);
      setError(parsed.publicMessage || (
        err.response?.status === 400
          ? 'Unable to start this interrogation.'
          : 'Failed to prepare interrogation. Please try again.'
      ));
      setErrorDetails(parsed);
      setLastFailedAction('init');
    } finally {
      setInitializing(false);
    }
  };

  // F4 recovery: the conduct request died client-side (timeout/abort) but the
  // server may have finished and committed the round. Poll interrogation
  // status (bounded) and, once the round shows committed, re-submit the
  // identical request — the resumable backend recognizes it as a replay and
  // returns the stored round without re-running generation. Returns the
  // replayed response, or null to fall back to the normal error UI.
  const recoverAbortedConduct = async (roundSubmitted, approachSubmitted) => {
    if (!approachSubmitted) return null;
    for (let attempt = 0; attempt <= INTERROGATION_ABORT_RECOVERY_MAX_POLLS; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, INTERROGATION_ABORT_RECOVERY_POLL_MS));
      }
      try {
        const status = await gameAPI.getInterrogationStatus(sessionId, suspect.id);
        if ((status?.rounds_completed || 0) >= roundSubmitted) {
          return await gameAPI.interrogate(sessionId, suspect.id, approachSubmitted, roundSubmitted);
        }
      } catch (recoveryErr) {
        console.warn('Interrogation abort-recovery attempt failed:', recoveryErr);
        return null;
      }
    }
    return null;
  };

  const handleInterrogate = async () => {
    if (!selectedApproach) return;

    // Tutorial: Emit "begin" button click event
    // Clamp to 1-2 range to prevent emitting non-existent event types
    if (onInterrogationAction) {
      const clampedRound = clampRound(currentRound);
      onInterrogationAction(clampedRound === 1 ? 'interrogation_begin_clicked' : 'interrogation_round2_begin_clicked', suspect?.id);
    }

    const roundSubmitted = currentRound;
    const approachSubmitted = selectedApproach;
    setLoading(true);
    setError(null);
    setErrorDetails(null);
    try {
      let response;

      if (isTutorial) {
        // Tutorial mode: Use pre-written narratives (no API calls)
        const narrativeKey = `interrogate_${suspect.id}`;
        const narrativeData = tutorialNarratives[narrativeKey];

        // Build response matching API format - use graceful fallback if narrative missing
        response = {
          success: true,
          narrative: narrativeData?.narrative || 'The suspect considers your questions carefully before responding...',
          effectiveness: 0.65, // Medium effectiveness for tutorial
          effectiveness_feedback: narrativeData ? 'Your questioning revealed some useful information.' : 'The suspect remained guarded.',
          revealed: narrativeData?.revealed_info || {
            new_clues: [],
            new_locations: [],
            new_documents: [],
            new_suspects: []
          },
          round_completed: currentRound,
          can_continue: currentRound < 2 // Allow 2 rounds in tutorial
        };
      } else {
        // Real game: Use API
        response = await gameAPI.interrogate(sessionId, suspect.id, approachSubmitted, roundSubmitted);
      }

      setResult(response);

      // If can continue, prepare for Round 2
      if (response.can_continue) {
        setCurrentRound(2);
      }

      // Only clear the selected approach on a successful round.
      // On failure we keep it so the Try Again button can resubmit it.
      setSelectedApproach(null);
    } catch (err) {
      console.error('Interrogation failed:', err);
      let recovered = null;
      if (!isTutorial && !err.response && (err.isTimeout || err.isNetworkError)) {
        // Client-side abort of a possibly-still-running server request (F4).
        recovered = await recoverAbortedConduct(roundSubmitted, approachSubmitted);
      }
      if (recovered) {
        setResult(recovered);
        if (recovered.can_continue) {
          setCurrentRound(2);
        }
        setSelectedApproach(null);
      } else {
        const parsed = parseActionError(err);
        setError(parsed.publicMessage || err.message || 'Interrogation failed. Please try again.');
        setErrorDetails(parsed);
        setLastFailedAction('conduct');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (errorDetails?.retryScope === 'refetch_session') {
      return;
    }
    const action = lastFailedAction;
    setError(null);
    setErrorDetails(null);
    setLastFailedAction(null);
    if (action === 'init') {
      initializeInterrogation();
    } else if (action === 'conduct' && selectedApproach) {
      handleInterrogate();
    } else {
      // Fallback: if we don't know which path failed, prefer re-init when
      // we don't yet have a personality hint loaded; otherwise retry conduct.
      if (!personalityHint) {
        initializeInterrogation();
      } else if (selectedApproach) {
        handleInterrogate();
      }
    }
  };

  const handleRefetchSession = async () => {
    if (typeof onRefetchSession === 'function') {
      const refreshed = await onRefetchSession();
      if (refreshed === false) {
        return;
      }
    } else if (typeof window !== 'undefined' && window.location?.reload) {
      window.location.reload();
      return;
    }

    // Refetch succeeded. The modal stays mounted across a session refetch (no key
    // prop; the parent doesn't toggle it closed), so reset stale interrogation +
    // error state here to keep it from leaking into the next run. Mirrors
    // closeAndReset's reset values.
    setError(null);
    setErrorDetails(null);
    setLastFailedAction(null);
    setResult(null);
    setAllResults([]);
    setSelectedApproach(null);
    setPersonalityHint('');
    setCurrentRound(clampRound((initialRoundsCompleted || 0) + 1));
  };

  const handleContinue = () => {
    // Tutorial: Emit continue button click event
    if (onInterrogationAction) {
      onInterrogationAction('interrogation_continue_clicked', suspect?.id);
    }

    // Store Round 1 result for later, DON'T call onComplete yet
    if (result) {
      setAllResults(prev => [...prev, result]);
    }
    // Stay in modal for Round 2
    setResult(null);
    setSelectedApproach(null);
  };

  const handleEndInterrogation = () => {
    // Tutorial: Emit complete button click event
    if (onInterrogationAction) {
      onInterrogationAction('interrogation_complete_clicked', suspect?.id);
    }

    closeAndReset();
  };

  const handleClose = () => {
    closeAndReset();
  };

  // Tutorial mode: Radix `pointerdown`-outside bypasses TutorialOverlay's click
  // blocker. Without this guard, an accidental backdrop tap closes the modal
  // mid-interrogation and leaves the user stuck. Explicit close buttons
  // (Complete Interrogation, End for Now, Close on error) call handleClose
  // directly, so they remain functional.
  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen && isTutorial) {
      return;
    }
    if (!isOpen) {
      handleClose();
    }
  };

  const closeAndReset = () => {
    // Collect all results including current one. Read-only "already complete"
    // envelopes are render-only — never pushed through onComplete (that would
    // duplicate dialogue entries for rounds the store may already have).
    const collected = result ? [...allResults, result] : allResults;
    const resultsToSave = collected.filter((r) => !r?.already_complete);

    // Combine all narratives into one result for single onComplete call
    // This ensures tutorial step only advances once
    if (resultsToSave.length > 0) {
      const combinedResult = {
        ...resultsToSave[resultsToSave.length - 1], // Use last result as base
        narrative: resultsToSave.map(r => r.narrative).join('\n\n---\n\n'),
        allNarratives: resultsToSave.map(r => r.narrative), // Keep individual narratives too
        allResults: resultsToSave // Pass all complete results for proper dialogue storage
      };
      onComplete(combinedResult);
    }

    onClose();
    // Reset state
    setResult(null);
    setAllResults([]);
    setSelectedApproach(null);
    setPersonalityHint('');
    setCurrentRound(1);
    setError(null);
    setErrorDetails(null);
    setLastFailedAction(null);
  };

  const retryAllowedByContract =
    errorDetails?.retryable === true && errorDetails.retryScope !== 'refetch_session';
  const canRefetchSession =
    errorDetails?.retryable === true && errorDetails.retryScope === 'refetch_session';
  const canRetryCurrentError = retryAllowedByContract && (
    lastFailedAction === 'init' ||
    (lastFailedAction === 'conduct' && selectedApproach)
  );
  const retryLabel = (
    errorDetails?.exactRetrySafe === false ||
    errorDetails?.retryScope === 'regenerate_field' ||
    errorDetails?.retryScope === 'regenerate_surface'
  )
    ? 'Regenerate response'
    : 'Try Again';

  if (!suspect) return null;

  // Show result view
  if (result) {
    const resultFit = approachFit(result.effectiveness);

    return (
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="w-full h-[100dvh] sm:max-w-3xl sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-slate-900 border-2"
          style={{ borderColor: themeAccent }}
          hideCloseButton={isTutorial}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 pr-10">
              <MessageSquare className="w-5 h-5" style={{ color: themeAccent }} />
              {/* Clamp to 1-2 range to prevent mislabeled UI */}
              Interrogation - Round {clampRound(result.round_completed)}
            </DialogTitle>
          </DialogHeader>

          {/* Approach fit — a label, not a bare percent the player cannot act
              on. The tutorial is out of scope for P3 and keeps the pre-P3
              block verbatim, fabricated 0.65 and all. */}
          <div className="mb-4 p-3 rounded-lg bg-slate-800">
            {isTutorial ? (
              <div className="flex items-center gap-2 mb-2">
                {result.effectiveness >= 0.7 ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : result.effectiveness >= 0.4 ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                )}
                <span className="text-sm font-medium text-white">
                  {result.effectiveness >= 0.7 ? 'Highly Effective' :
                   result.effectiveness >= 0.4 ? 'Partially Effective' : 'Ineffective'}
                </span>
                <span className="text-xs text-slate-400 ml-auto">
                  {Math.round(result.effectiveness * 100)}%
                </span>
              </div>
            ) : resultFit && (
              <div className="flex items-center gap-2 mb-2" data-testid="approach-fit">
                {resultFit.tone === 'positive' ? (
                  <CheckCircle className={`w-5 h-5 ${TONE_CLASSES[resultFit.tone].text}`} />
                ) : (
                  <AlertTriangle className={`w-5 h-5 ${TONE_CLASSES[resultFit.tone].text}`} />
                )}
                <span className={`text-sm font-medium ${TONE_CLASSES[resultFit.tone].text}`}>
                  {resultFit.label}
                </span>
                <span className="text-xs text-slate-400 ml-auto">{APPROACH_FIT_CAPTION}</span>
              </div>
            )}
            <p className="text-sm text-slate-300">{result.effectiveness_feedback}</p>
          </div>

          {/* Narrative */}
          <div className="bg-slate-800 rounded-lg p-4 mb-4">
            <div className="prose prose-invert prose-sm max-w-none">
              {result.narrative.split('\n').map((paragraph, index) => (
                <p key={index} className="text-slate-200 mb-3 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Revealed info */}
          {(result.revealed.new_clues?.length > 0 ||
            result.revealed.new_locations?.length > 0 ||
            result.revealed.new_documents?.length > 0) && (
            <div className="mb-4 p-3 rounded-lg bg-green-900/30 border border-green-700">
              <h4 className="text-sm font-semibold text-green-400 mb-2">New Information Revealed:</h4>
              <ul className="text-sm text-green-300 space-y-1">
                {result.revealed.new_clues?.map((clueId) => (
                  <li key={clueId}>New clue discovered</li>
                ))}
                {result.revealed.new_locations?.map((locId) => (
                  <li key={locId}>New location discovered</li>
                ))}
                {result.revealed.new_documents?.map((docId) => (
                  <li key={docId}>New document discovered</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {result.can_continue ? (
              <>
                <Button
                  onClick={handleContinue}
                  className={`flex-1 min-h-touch font-semibold ${isTutorial ? 'ring-4 ring-amber-400 animate-pulse' : ''}`}
                  style={{ backgroundColor: themeAccent }}
                  data-tutorial="continue-interrogation"
                >
                  <span className="hidden sm:inline">Continue Questioning (Round 2)</span>
                  <span className="sm:hidden">Continue (Round 2)</span>
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  onClick={handleEndInterrogation}
                  variant="outline"
                  className="flex-1 min-h-touch border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  End for Now
                </Button>
              </>
            ) : (
              <Button
                onClick={handleEndInterrogation}
                className={`w-full min-h-touch font-semibold ${isTutorial ? 'ring-4 ring-amber-400 animate-pulse' : ''}`}
                style={{ backgroundColor: themeAccent }}
                data-tutorial="complete-interrogation"
              >
                Complete Interrogation
              </Button>
            )}
          </div>

          {!result.can_continue && (
            <p className="text-xs text-center text-slate-500 mt-2">
              This suspect cannot be questioned further.
            </p>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Show approach selection view
  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="w-full h-[100dvh] sm:max-w-2xl sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-slate-900 border-2"
        style={{ borderColor: themeAccent }}
        hideCloseButton={isTutorial}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg sm:text-xl font-bold text-white pr-10">
            Interrogate {suspect.name}
          </DialogTitle>
        </DialogHeader>

        {error ? (
          <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
            <p className="text-red-300">{error}</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              {canRetryCurrentError && (
                <Button
                  onClick={handleRetry}
                  className="font-semibold"
                  style={{ backgroundColor: themeAccent }}
                >
                  {retryLabel}
                </Button>
              )}
              {canRefetchSession && (
                <Button
                  onClick={handleRefetchSession}
                  className="font-semibold"
                  style={{ backgroundColor: themeAccent }}
                >
                  Reload case
                </Button>
              )}
              <Button
                onClick={handleClose}
                className="border-slate-600 text-white hover:bg-slate-700"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        ) : initializing ? (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: themeAccent }} />
            <p className="text-slate-400">Preparing interrogation...</p>
            <p className="text-xs text-slate-500">Analyzing suspect profile</p>
          </div>
        ) : (
          <>
            {/* Round indicator */}
            <div className="mb-4 text-center">
              <span className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300">
                {/* Clamp to 1-2 range to prevent mislabeled UI */}
                Round {clampRound(currentRound)} of 2
              </span>
            </div>

            {/* Personality hint */}
            {personalityHint && (
              <div className="mb-4 p-3 bg-slate-800 rounded-lg border-l-4" style={{ borderLeftColor: themeAccent }}>
                <p className="text-sm text-slate-300 italic">
                  {personalityHint}
                </p>
              </div>
            )}

            {/* Suspect info */}
            <div className="mb-4 flex items-center gap-3">
              {suspect.portrait ? (
                <img
                  src={suspect.portrait}
                  alt={suspect.name}
                  className="w-16 h-16 rounded-full object-cover border-2"
                  style={{ borderColor: themeAccent }}
                />
              ) : (
                <div
                  className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center border-2"
                  style={{ borderColor: themeAccent }}
                >
                  <span className="text-2xl text-slate-400">
                    {suspect.name?.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-white">{suspect.name}</h3>
                <p className="text-sm text-slate-400">{suspect.occupation}</p>
              </div>
            </div>

            {/* Approach selection */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-slate-300 mb-3">
                Choose your approach:
              </h4>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {APPROACHES.map((approach) => {
                  const Icon = approach.icon;
                  const isSelected = selectedApproach === approach.id;
                  const isRecommended = recommendedApproach === approach.id;

                  return (
                    <button
                      key={approach.id}
                      onClick={() => {
                        setSelectedApproach(approach.id);
                        // Tutorial: Emit approach selection event
                        // Clamp to 1-2 range to prevent emitting non-existent event types
                        if (onInterrogationAction) {
                          const clampedRound = clampRound(currentRound);
                          onInterrogationAction(clampedRound === 1 ? 'interrogation_approach_selected' : 'interrogation_round2_approach_selected', suspect?.id);
                        }
                      }}
                      data-approach={approach.id}
                      className={`min-h-touch p-3 sm:p-4 rounded-lg border-2 transition-all text-left relative ${
                        isSelected
                          ? 'bg-slate-700 border-opacity-100'
                          : isRecommended
                          ? 'bg-slate-700/50 border-amber-400 animate-pulse'
                          : 'bg-slate-800 border-slate-700 hover:border-slate-500'
                      }`}
                      style={{
                        borderColor: isSelected ? approach.color : isRecommended ? '#fbbf24' : undefined,
                        boxShadow: isRecommended && !isSelected ? '0 0 20px rgba(251, 191, 36, 0.5)' : undefined
                      }}
                    >
                      {isRecommended && !isSelected && (
                        <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                          TIP
                        </span>
                      )}
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <Icon
                          className="w-5 h-5"
                          style={{ color: approach.color }}
                        />
                        <span className="font-semibold text-white text-sm sm:text-base">
                          {approach.name}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 hidden sm:block">
                        {approach.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected approach detail */}
            {selectedApproach && (
              <div className="mb-4 p-3 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-300">
                  {APPROACHES.find(a => a.id === selectedApproach)?.detail}
                </p>
              </div>
            )}

            {/* Begin button */}
            <Button
              onClick={handleInterrogate}
              disabled={!selectedApproach || loading}
              className={`w-full min-h-touch font-semibold ${isTutorial && selectedApproach ? 'ring-4 ring-amber-400 animate-pulse' : ''}`}
              style={{ backgroundColor: selectedApproach ? themeAccent : undefined }}
              data-tutorial="begin-interrogation"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Interrogating...
                </>
              ) : (
                /* Clamp to 1-2 range to prevent mislabeled button text */
                `Begin ${clampRound(currentRound) === 1 ? 'Interrogation' : 'Round 2'}`
              )}
            </Button>

            {/* Clamp to 1-2 range to prevent showing message incorrectly */}
            {clampRound(currentRound) === 2 && (
              <p className="text-xs text-center text-slate-500 mt-2">
                This is your final round with this suspect.
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
